import { last } from 'lodash';
import {
  type Editor,
  Editor as SlateEditor,
  type Element,
  type Location,
  Node as SlateNode,
  type NodeEntry,
  Text as SlateText,
} from 'slate';

interface CharPoint {
  char: string;
  path: Location;
  offset: number;
}

const getNodeCharactersPoints = (editor: Editor, nodePath: Location): CharPoint[] => {
  const textNodes = SlateEditor.nodes(editor, {
    at: SlateEditor.range(editor, nodePath),
    match: (node) => SlateText.isText(node),
  });

  return [...textNodes]
    .map(([node, path]) => {
      const text = SlateNode.string(node);
      const chars = text.split('');
      return chars.map((char, index) => ({ char, path, offset: index }));
    })
    .flat();
};

export const getCharactersPointsBoundaries = (
  charactersPoints: CharPoint[],
  start: number,
  end: number
): { start: CharPoint; end: CharPoint } | null => {
  if (!charactersPoints || !(start >= 0 && end >= 0)) {
    return null;
  }

  const startCharData = charactersPoints[start];
  let endCharData = charactersPoints[end];
  if (!endCharData) {
    const lastCharData = last(charactersPoints);
    if (lastCharData) {
      endCharData = {
        char: lastCharData.char,
        path: lastCharData.path,
        offset: lastCharData.offset + 1,
      };
    }
  }

  return {
    start: startCharData,
    end: endCharData,
  };
};

export const getParagraphsCharacterPoints = (
  { editor, paragraphs }: { editor: Editor; paragraphs: NodeEntry<Element>[] },
  options?: {
    skipNewlineSplitting?: boolean;
  }
): CharPoint[][] => {
  const result = [];

  // Get Slate paragraphs - element of type PARAGRAPH
  const slateParagraphsData = paragraphs.map(([, path]) => getNodeCharactersPoints(editor, path));

  if (!options?.skipNewlineSplitting) {
    // Each Slate paragraph can contain line-breaks, which we should split into real text paragraphs
    slateParagraphsData.forEach((paragraphData) => {
      let currentParagraph: CharPoint[] = [];

      paragraphData.forEach((charData) => {
        if (charData.char === '\n') {
          result.push(currentParagraph);
          currentParagraph = [];
        } else {
          currentParagraph.push(charData);
        }
      });

      result.push(currentParagraph);
    });
  } else {
    result.push(...slateParagraphsData);
  }

  return result;
};
